/**
 * Workspace Service
 * Handles workspace-related database operations using Supabase
 *
 * @module services/workspace/workspaceService
 */

import { getSupabaseClient, isSupabaseConfigured } from "@/config/supabase";
import { asWorkspaceRole } from "./workspaceRole";
import { DEMO_MODE, DEMO_WORKSPACE } from "@/config/demoConfig";
import { info, logError } from '../logger';
import { readAllRows } from "@/services/supabasePaging";
import type { Workspace } from "@/types";

/**
 * Demo workspace returned when Supabase is not configured and DEMO_MODE is true
 */
const DEMO_WORKSPACE_DATA: Workspace = {
  id: DEMO_WORKSPACE.id,
  name: DEMO_WORKSPACE.name,
  slug: DEMO_WORKSPACE.slug,
  userRole: "admin",
};

/**
 * Raw database response type for workspace_members join
 */
interface WorkspaceMemberRow {
  /** text + CHECK in the database, so narrowed at the boundary, not asserted. */
  role: string;
  /**
   * A SINGLE object, not an array.
   *
   * The old declaration said `[] | null` and a comment above
   * transformToWorkspace said "Supabase returns workspaces as an array with a
   * single element". Measured against the running API:
   *
   *   GET /workspace_members?select=role,workspaces(id,name,slug,...)
   *   -> "workspaces": { "id": "...", "name": "Demo Workspace", ... }
   *
   * an object, because the embed is many-to-one. The generated types agree. The
   * code only ever worked because transformToWorkspace tested Array.isArray
   * first and fell through to the object.
   */
  workspaces: {
    id: string;
    name: string;
    slug: string;
    created_at?: string;
    updated_at?: string;
  } | null;
}

/**
 * Transform database row to Workspace type
 */
const transformToWorkspace = (row: WorkspaceMemberRow): Workspace | null => {
  // One object. See the note on WorkspaceMemberRow.workspaces for the request
  // that settled this; the Array.isArray branch that used to be here was
  // guarding against a shape the API does not return.
  const workspace = row.workspaces;

  if (!workspace) return null;

  return {
    id: workspace.id,
    name: workspace.name,
    slug: workspace.slug,
    userRole: asWorkspaceRole(row.role),
    createdAt: workspace.created_at,
    updatedAt: workspace.updated_at,
  };
};

/**
 * Fetch the current user's workspace with their role
 *
 * NO CALLERS today — it is only re-exported from workspace/index.ts. Two things
 * to know before wiring it to anything:
 *
 * 1. It does NOT carry the platform-master branch its sibling getUserWorkspaces
 *    has, so the two disagree about the same user: getUserWorkspaces hands a
 *    master every workspace, this returns one.
 * 2. "Uses RLS to automatically filter by authenticated user" — which this said
 *    until now — is FALSE for a master. The select policy on workspace_members
 *    is `workspace_id = ANY(get_user_workspace_ids())`, and that function
 *    returns EVERY workspace when is_platform_master(). Measured with the real
 *    session of the master on this machine: unfiltered, this query answered
 *    "Demo Workspace" — a workspace they hold no membership row in, carrying
 *    ANOTHER USER'S role of admin. Narrowed to them it answers "YDM Workspace",
 *    their own oldest membership. 31 rows visible, 11 of them theirs.
 *
 * So `.eq("user_id", user.id)` below is load-bearing, exactly as it is in
 * getUserWorkspaces, and for the same reason: this table is scoped to "every
 * member of every workspace you can see", which is the wrong question.
 *
 * @returns Workspace data with user role, or null if not found
 */
export const getCurrentUserWorkspace = async (): Promise<Workspace | null> => {
  if (!isSupabaseConfigured()) {
    if (DEMO_MODE) {
      return DEMO_WORKSPACE_DATA;
    }
    return null;
  }

  const supabase = getSupabaseClient();

  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      info("WorkspaceService", "No authenticated user");
      return null;
    }

    // Order by created_at to get the first workspace the user joined
    // (deterministic). The user_id filter is not belt-and-braces — see the note
    // above this function.
    const { data, error } = await supabase
      .from("workspace_members")
      .select(
        `
        role,
        workspaces (
          id,
          name,
          slug,
          created_at,
          updated_at
        )
      `
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) {
      logError("WorkspaceService", "Error fetching workspace", error);
      return null;
    }

    if (!data) {
      info("WorkspaceService", "No workspace found for current user");
      return null;
    }

    return transformToWorkspace(data as WorkspaceMemberRow);
  } catch (error) {
    logError("WorkspaceService", "Failed to fetch workspace", error);
    return null;
  }
};

/**
 * Fetch all workspaces the current user belongs to
 * Used for workspace switcher functionality
 *
 * @returns Array of workspace data with user roles
 */
export const getUserWorkspaces = async (): Promise<Workspace[]> => {
  if (!isSupabaseConfigured()) {
    if (DEMO_MODE) {
      return [DEMO_WORKSPACE_DATA];
    }
    return [];
  }

  const supabase = getSupabaseClient();

  try {
    // First check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      info("WorkspaceService", "No authenticated user");
      return [];
    }

    // Platform master: return EVERY workspace, not only the ones they belong to,
    // so the switcher lists all of them and the master can switch into any.
    // 20240101000023 lets the master read every workspaces row and read/write
    // that workspace's media, so this is a plain select and the data pages render.
    // Non-masters fall straight through to the member-scoped query below.
    const { data: isMaster } = await supabase.rpc("is_platform_master");
    if (isMaster === true) {
      // Paged, and tie-broken on id. Unpaged this stopped at PostgREST's
      // 1000-row cap with HTTP 200 and no error, so a master's switcher would
      // quietly omit workspaces they are entitled to see. created_at is not
      // unique, and paging an ORDER BY with duplicate keys drops rows at page
      // boundaries and repeats others.
      //
      // Ten workspaces today, so this is a ceiling rather than a live loss.
      // Note for whoever meets it: past a thousand, the switcher wants a search
      // box, not a longer list — this makes the read correct, not the UI.
      const { rows: allRows, error: allError } = await readAllRows<{
        id: string; name: string; slug: string; created_at: string; updated_at: string;
      }>((from, to) =>
        supabase
          .from("workspaces")
          .select("id, name, slug, created_at, updated_at")
          .order("created_at", { ascending: true })
          .order("id", { ascending: true })
          .range(from, to),
        "master workspace list",
      );

      if (allError) {
        logError("WorkspaceService", "Error fetching all workspaces for master", allError);
        return [];
      }

      return allRows.map((w): Workspace => ({
        id: w.id,
        name: w.name,
        slug: w.slug,
        userRole: "admin",
        createdAt: w.created_at,
        updatedAt: w.updated_at,
      }));
    }

    // `.eq("user_id", user.id)` is load-bearing, not belt-and-braces. RLS scopes
    // this table to "every member of every workspace you belong to" — which is
    // the right policy for a member list and the wrong one for "which
    // workspaces am I in". Without the filter the query returns one row per
    // MEMBER, so a workspace shows up once per person in it: a two-person
    // workspace appeared twice in the switcher, a five-person agency workspace
    // would appear five times. React reported it as duplicate keys, which is
    // the same defect wearing a different hat — duplicate keys let a list
    // duplicate or omit children.
    //
    // It stayed invisible because the switcher renders a plain label when a
    // user has fewer than two workspaces, so the list was never mapped until
    // someone belonged to a second one.
    const { rows: data, error } = await readAllRows<WorkspaceMemberRow>((from, to) =>
      supabase
        .from("workspace_members")
        .select(
          `
        role,
        workspaces (
          id,
          name,
          slug,
          created_at,
          updated_at
        )
      `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to),
      "workspace memberships",
    );

    if (error) {
      logError("WorkspaceService", "Error fetching workspaces", error);
      return [];
    }

    if (data.length === 0) {
      return [];
    }

    // Transform to clean workspace array, filtering out null workspaces
    return data
      .map(transformToWorkspace)
      .filter((ws): ws is Workspace => ws !== null);
  } catch (error) {
    logError("WorkspaceService", "Failed to fetch workspaces", error);
    return [];
  }
};

/**
 * Check if workspace service is configured
 * Delegates to the main Supabase configuration check
 */
export const isWorkspaceServiceConfigured = (): boolean => {
  return isSupabaseConfigured();
};

/**
 * Get workspace service configuration status
 */
export const getWorkspaceServiceConfig = () => {
  const configured = isSupabaseConfigured();
  return {
    configured,
  };
};

export default {
  getCurrentUserWorkspace,
  getUserWorkspaces,
  isWorkspaceServiceConfigured,
  getWorkspaceServiceConfig,
};
